import {
  Activity,
  FileText,
  CheckSquare,
  MessageSquare,
  Calendar,
  Flag,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import React from 'react';
import { useGetActivityFeedQuery } from '../../services/dashboardApi';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

const ActivityFeedWidget: React.FC<WidgetProps> = ({ scope, projectId, size }) => {
  const { data, isLoading, error, refetch, isFetching } = useGetActivityFeedQuery(
    {
      projectId: scope === 'project' ? projectId : undefined,
      limit: size === '2x2' ? 10 : size === '2x1' ? 6 : 4,
    },
    { pollingInterval: 300000 }
  );

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'task':
        return <CheckSquare className="h-4 w-4 text-blue-500" />;
      case 'document':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'meeting':
        return <Calendar className="h-4 w-4 text-purple-500" />;
      case 'comment':
        return <MessageSquare className="h-4 w-4 text-orange-500" />;
      case 'milestone':
        return <Flag className="h-4 w-4 text-red-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={5} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">Failed to load activity</p>
          <button
            onClick={() => refetch()}
            className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center justify-center mx-auto"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data?.activities || data.activities.length === 0) {
    return (
      <WidgetEmptyState
        title="No Recent Activity"
        description="Activity will appear here as your team works"
        icon={Activity}
      />
    );
  }

  const isCompact = size === '1x1';

  return (
    <div className="space-y-1">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">
          {scope === 'org' ? 'Organization Activity' : 'Project Activity'}
        </span>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Activity List */}
      <div className="space-y-3">
        {data.activities.map(activity => (
          <div key={activity.id} className="flex items-start space-x-3">
            {/* Icon */}
            <div className="flex-shrink-0 mt-0.5">{getActivityIcon(activity.type)}</div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Action & Description */}
                  <p className="text-xs text-gray-900">
                    <span className="font-medium">{activity.user.name}</span>{' '}
                    <span className="text-gray-600">{activity.action}</span>
                  </p>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{activity.description}</p>

                  {/* Project (for org scope) */}
                  {scope === 'org' && activity.project && !isCompact && (
                    <p className="text-xs text-blue-600 mt-0.5 truncate">{activity.project.name}</p>
                  )}
                </div>

                {/* Timestamp */}
                <span className="flex-shrink-0 text-xs text-gray-400 ml-2">
                  {formatTimeAgo(activity.timestamp)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show More Indicator */}
      {data.total > data.activities.length && (
        <div className="pt-2 mt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            +{data.total - data.activities.length} more activities
          </p>
        </div>
      )}
    </div>
  );
};

export default ActivityFeedWidget;
